import { useEffect, useRef, useState } from "react";
import { Sparkles, Headphones, Play, Pause, Trash2 } from "lucide-react";
import { listPodcasts, genPodcast, podcastAudioUrl, deletePodcast } from "@/lib/api";
import { toast } from "sonner";

export default function PodcastsTab({ subject }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [style, setStyle] = useState("dialogue");
  const [tone, setTone] = useState("casual");
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await listPodcasts(subject.id)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [subject.id]);

  const generate = async () => {
    setGenerating(true);
    try {
      await genPodcast(subject.id, style, tone);
      toast.success("Podcast generated! Audio is ready.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const togglePlay = (p) => {
    if (playing === p.id) {
      audioRef.current?.pause();
      setPlaying(null);
    } else {
      setPlaying(p.id);
      // audio src is set declaratively below
      setTimeout(() => audioRef.current?.play().catch(() => {}), 50);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this podcast?")) return;
    await deletePodcast(id);
    if (playing === id) setPlaying(null);
    load();
  };

  const playingItem = items.find((i) => i.id === playing);

  return (
    <div className="space-y-6">
      <div className="nb-card p-5 grid sm:grid-cols-3 gap-4 items-end">
        <div>
          <label className="label-caps block mb-2">Format</label>
          <div className="flex gap-2">
            {[
              { v: "dialogue", l: "2-Person Chat" },
              { v: "teacher", l: "Teacher" },
            ].map((o) => (
              <button
                key={o.v}
                data-testid={`pod-style-${o.v}`}
                onClick={() => setStyle(o.v)}
                className={`nb-border rounded-xl px-3 py-2 font-bold text-sm flex-1 ${
                  style === o.v ? "bg-[#D4C4FB] dark:bg-[#B794F4] nb-shadow text-black" : "bg-white dark:bg-zinc-900"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="label-caps block mb-2">Tone</label>
          <div className="flex gap-2">
            {[
              { v: "casual", l: "Casual" },
              { v: "exam", l: "Exam" },
              { v: "simple", l: "Simple" },
            ].map((o) => (
              <button
                key={o.v}
                data-testid={`pod-tone-${o.v}`}
                onClick={() => setTone(o.v)}
                className={`nb-border rounded-xl px-3 py-2 font-bold text-xs flex-1 ${
                  tone === o.v ? "bg-[#FFE066] dark:bg-[#F6E05E] nb-shadow text-black" : "bg-white dark:bg-zinc-900"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
        <button
          data-testid="generate-podcast-btn"
          onClick={generate}
          disabled={generating}
          className="nb-button px-5 py-3 bg-[#9DFFB0] dark:bg-[#68D391] flex items-center justify-center gap-2"
        >
          <Sparkles className="w-5 h-5" strokeWidth={3} />
          {generating ? "Generating…" : "Generate Podcast"}
        </button>
      </div>

      {loading ? (
        <div className="font-bold text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="nb-card p-8 text-center">
          <Headphones className="w-10 h-10 mx-auto mb-2" />
          <div className="font-display text-2xl font-extrabold mb-1">No podcasts yet</div>
          <p className="text-zinc-600 dark:text-zinc-400">Generate one above (uses your subject&apos;s notes).</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((p) => {
            const isPlaying = playing === p.id;
            return (
              <div key={p.id} data-testid={`podcast-${p.id}`} className="nb-card p-5">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => togglePlay(p)}
                    data-testid={`pod-play-${p.id}`}
                    className="nb-button w-14 h-14 rounded-full flex items-center justify-center bg-[#FFE066] dark:bg-[#F6E05E]"
                  >
                    {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl font-extrabold truncate">{p.title}</div>
                    <div className="text-xs font-bold text-zinc-500 mt-1">
                      {p.style === "dialogue" ? "2-person" : "teacher"} · {p.tone} · {p.duration_lines} lines
                    </div>
                  </div>
                  <button onClick={() => remove(p.id)} className="nb-border rounded-lg p-2 hover:bg-red-100 dark:hover:bg-red-900">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {isPlaying && (
                  <details className="mt-4">
                    <summary className="font-bold cursor-pointer text-sm">Show transcript</summary>
                    <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-2">
                      {p.lines?.map((l, i) => (
                        <div key={i} className="text-sm">
                          <span className="font-extrabold">{l.speaker}:</span> <span className="font-medium">{l.text}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}

      {playingItem && (
        <audio
          ref={audioRef}
          src={podcastAudioUrl(playingItem.id)}
          onEnded={() => setPlaying(null)}
          controls
          className="fixed bottom-4 right-4 nb-border rounded-xl bg-white dark:bg-zinc-900 nb-shadow z-30 max-w-[calc(100%-2rem)]"
        />
      )}
    </div>
  );
}
